import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { eur } from "@/lib/format";
import { useT } from "@/i18n";
import { PageHeader, StatCard } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  ChevronLeft, Users, Wallet, CheckCircle2, PenLine, CalendarCheck, Package, ListChecks, MessageSquare, Sparkles, HeartPulse,
} from "lucide-react";

const META = {
  inner: { title: "Inner Circle", accent: "bg-teal-50 text-teal-600", eyebrow: "Inner Circle" },
  local: { title: "Local Circle", accent: "bg-blue-50 text-blue-600", eyebrow: "Local Circle" },
};
const TILE_NAV = [
  { id: "deliverables", labelKey: "ctl.deliverables", icon: Package },
  { id: "todos", labelKey: "ctl.todos", icon: ListChecks },
  { id: "clients", labelKey: "ctl.clients", icon: Users },
  { id: "chats", labelKey: "ctl.chats", icon: MessageSquare },
  { id: "weva", labelKey: "cdb.weva", icon: Sparkles },
  { id: "members", labelKey: "ctl.members", icon: HeartPulse },
];

export default function CircleDashboard({ type }) {
  const t = useT();
  const { user } = useAuth();
  const isLocal = type === "local";
  const [circleId, setCircleId] = useState("");
  const [overview, setOverview] = useState(null);
  const [locals, setLocals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isLocal || !user || user.role !== "coach") return;
    api.get("/coach/circles/local-list").then(({ data }) => setLocals(data?.circles || [])).catch(() => {});
  }, [isLocal, user]);

  useEffect(() => {
    if (!user || user.role !== "coach") return;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ circle_type: type });
    if (isLocal && circleId) params.set("circle_id", circleId);
    api.get(`/coach/circles/overview?${params.toString()}`)
      .then(({ data }) => { if (!cancelled) setOverview(data); })
      .catch((err) => { if (!cancelled) toast.error(apiError(err, t("cdb.load_error"))); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [type, isLocal, circleId, user]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const meta = META[type] || META.inner;
  const o = overview || {};
  const tiles = [
    { label: t("cdb.active_members"), value: o.active_members ?? 0, icon: Users, testId: "tile-active-members" },
    { label: t("cdb.earnings_month"), value: eur(o.month_earnings_cents ?? 0), icon: Wallet, testId: "tile-earnings" },
    { label: t("cdb.snapshots_confirmed"), value: o.snapshot_requests_confirmed ?? 0, icon: CheckCircle2, testId: "tile-snapshots" },
    { label: t("cdb.entries_received"), value: o.user_entries_received ?? 0, icon: PenLine, testId: "tile-entries" },
    { label: t("cdb.bookings"), value: o.bookings_received ?? 0, icon: CalendarCheck, testId: "tile-bookings" },
    { label: t("cdb.active_offerings"), value: (o.active_offerings || []).length, icon: Package, testId: "tile-offerings" },
  ];

  return (
    <div data-testid={`circle-dashboard-${type}`}>
      <Seo title={t("cdb.seo", { title: meta.title })} noindex />
      <Link to="/app/coach/circles" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="dashboard-back">
        <ChevronLeft className="h-4 w-4" /> {t("cir.title")}
      </Link>
      <PageHeader eyebrow={meta.eyebrow} title={meta.title} subtitle={t("cdb.subtitle")} />

      {isLocal && (
        <div className="mb-5 flex gap-2 overflow-x-auto pb-1">
          <button
            type="button"
            onClick={() => setCircleId("")}
            data-testid="local-scope-all"
            className={`shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors ${!circleId ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}
          >
            {t("cdb.all_circles")}
          </button>
          {locals.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => setCircleId(c.id)}
              data-testid={`local-scope-${c.id}`}
              className={`shrink-0 rounded-full border px-3 py-1.5 text-sm transition-colors ${circleId === c.id ? "border-primary bg-primary/5 text-foreground" : "border-border text-muted-foreground hover:bg-accent"}`}
            >
              {c.name} · {c.member_count}
            </button>
          ))}
        </div>
      )}

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-28 rounded-2xl" />)}
        </div>
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {tiles.map((st) => <StatCard key={st.testId} {...st} />)}
          </div>

          <div className="mt-6 rounded-2xl border border-border bg-card p-5">
            <p className="mb-3 text-sm font-semibold text-foreground">{t("cdb.included_deliverables")}</p>
            {(o.deliverables || []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("cdb.no_deliverables_defined")}</p>
            ) : (
              <ul className="space-y-1.5" data-testid="deliverables-preview">
                {o.deliverables.map((d, i) => (
                  <li key={i} className="flex items-center justify-between rounded-xl bg-secondary/60 px-3 py-2 text-sm">
                    <span className="text-foreground">{d.kind}</span>
                    <span className="text-muted-foreground">{t("cdb.per_month", { n: d.count_per_month })}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="mt-6 flex flex-wrap gap-4" data-testid="dashboard-footer-totals">
            <div className="rounded-2xl border border-border bg-card px-5 py-3">
              <p className="text-xs text-muted-foreground">{t("cdb.earnings_this_month")}</p>
              <p className="font-display text-xl font-semibold text-foreground">{eur(o.month_earnings_cents ?? 0)}</p>
            </div>
            <div className="rounded-2xl border border-border bg-card px-5 py-3">
              <p className="text-xs text-muted-foreground">{t("cdb.bookings_received")}</p>
              <p className="font-display text-xl font-semibold text-foreground">{o.bookings_received ?? 0}</p>
            </div>
          </div>

          <div className="mt-8">
            <p className="mb-3 font-display text-lg text-foreground">{t("cdb.areas")}</p>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {TILE_NAV.map((nav) => (
                <Link
                  key={nav.id}
                  to={`/app/coach/circles/${type}/tile/${nav.id}${circleId ? `?circleId=${circleId}` : ""}`}
                  data-testid={`circle-tile-${nav.id}`}
                  className="group flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:bg-accent/40"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-foreground"><nav.icon className="h-5 w-5" /></div>
                  <span className="font-medium text-foreground">{t(nav.labelKey)}</span>
                </Link>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
